/**
 * The canvas mesh picker: one set of delegated pointer handlers, for the single root group in
 * `NodeDispatcher`, that drive `<SelectionContext>`. A pointer-up selects only when travel stayed
 * within `dragThresholdPx`, so a navigation drag does not select.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelection } from '../contexts/SelectionContext.js';
import { resolvePathFromObject } from './resolvePathFromObject.js';
import { getAncestorPaths } from '../../utils/nodePath.js';
const DEFAULT_DRAG_THRESHOLD_PX = 5;
export function useViewportSelection(options = {}) {
    const { dragThresholdPx = DEFAULT_DRAG_THRESHOLD_PX, autoExpandAncestors = true } = options;
    const { setSelectedNodePath, hoverStore, expandedNodePaths, setExpandedNodePaths, objectPathMap } = useSelection();
    const downPosRef = useRef(null);
    const expandedRef = useRef(expandedNodePaths);
    useEffect(() => {
        expandedRef.current = expandedNodePaths;
    }, [expandedNodePaths]);
    const select = useCallback((nodePath) => {
        setSelectedNodePath(nodePath);
        if (autoExpandAncestors) {
            const ancestors = getAncestorPaths(nodePath);
            if (ancestors.length === 0)
                return;
            const current = expandedRef.current;
            const missing = ancestors.filter((a) => !current.has(a));
            if (missing.length > 0) {
                const next = new Set(current);
                for (const a of missing)
                    next.add(a);
                setExpandedNodePaths(next);
            }
        }
    }, [setSelectedNodePath, autoExpandAncestors, setExpandedNodePaths]);
    const handlers = useMemo(() => ({
        onPointerDown(e) {
            downPosRef.current = { x: e.clientX, y: e.clientY };
        },
        onPointerUp(e) {
            const down = downPosRef.current;
            downPosRef.current = null;
            if (!down)
                return;
            const dx = e.clientX - down.x;
            const dy = e.clientY - down.y;
            if (Math.hypot(dx, dy) > dragThresholdPx)
                return;
            const path = resolvePathFromObject(e.object, objectPathMap);
            if (!path)
                return;
            e.stopPropagation();
            select(path);
        },
        // Over and out fire once for the whole root, so move carries the per-mesh `e.object`. R3F
        // calls it per intersected mesh, nearest first, until `stopPropagation()`: without the stop
        // the farthest mesh wins. Stopping only on a resolved path lets an unregistered nearest hit,
        // such as the grid, fall through to a registered mesh behind it.
        onPointerMove(e) {
            const path = resolvePathFromObject(e.object, objectPathMap);
            hoverStore.set(path);
            if (path)
                e.stopPropagation();
        },
        onPointerOut() {
            hoverStore.set(null);
        },
    }), [dragThresholdPx, select, hoverStore, objectPathMap]);
    return useMemo(() => ({ handlers }), [handlers]);
}
//# sourceMappingURL=useViewportSelection.js.map